import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useTrackEvent } from '@/lib/posthog/hooks/useTrackEvent';
import {
  getAuthFailureReason,
  isInvalidTrialOfferError,
  trialOfferFailureMessage,
  trialOfferParamKey,
} from '@/modules/auth/constants';
import { useExternalLoginMutation } from '@/modules/auth/mutations/useExternalLoginMutation';
import {
  clearSlackSigninState,
  completeSlackLoginFromCallback,
  readSlackIntent,
  SlackSsoError,
} from '@/modules/auth/slackSso';
import {
  type AuthIntent,
  authProviders,
} from '@/modules/auth/types/externalLogin';
import { getRoleBasedReturnTo } from '@/modules/auth/utils/getRoleBasedReturnTo';

import {
  type SlackCallbackError,
  SlackCallbackStatus,
} from './slackCallbackStatus/SlackCallbackStatus';

const getSlackSsoErrorCode = (error: unknown) =>
  error instanceof SlackSsoError ? error.code : 'exchange_failed';

const getSlackSsoErrorMessage = (error: unknown) =>
  error instanceof Error
    ? error.message
    : 'Slack sign-in failed. Please try again.';

/**
 * `/auth/callback/slack`: exchanges Slack's code through the API's external
 * login. Signing in with Slack doesn't connect Finlay's bot to the workspace;
 * that stays the separate Connect Slack step (`SlackCallback`).
 */
export const SlackSignInCallback = () => {
  const navigate = useNavigate();
  const trackEvent = useTrackEvent();
  const externalLoginMutation = useExternalLoginMutation();
  const hasStarted = useRef(false);

  const [failure, setFailure] = useState<SlackCallbackError>();

  useEffect(() => {
    if (hasStarted.current) {
      return;
    }

    hasStarted.current = true;

    const completeSignIn = async () => {
      trackEvent('slackSSO', { stage: 'callback_received' });

      let trialOfferCode: string | undefined;
      // Set once the callback is validated; a failure before that reads it
      // from the stored state instead.
      let intent: AuthIntent | undefined;
      try {
        const callbackResult = await completeSlackLoginFromCallback(
          window.location.href,
        );
        intent = callbackResult.intent;

        const returnTo = new URL(
          callbackResult.returnTo,
          window.location.origin,
        );
        // As for LinkedIn (see LinkedInCallback.tsx), the offer code rides in
        // `returnTo`'s query string through the OIDC state; strip it so the
        // one-time code stays out of app URLs.
        trialOfferCode =
          returnTo.searchParams.get(trialOfferParamKey) ?? undefined;
        returnTo.searchParams.delete(trialOfferParamKey);

        await externalLoginMutation.mutateAsync({
          provider: authProviders.slack,
          authorizationCode: callbackResult.code,
          redirectUri: callbackResult.redirectUri,
          idToken: null,
          trialOfferCode,
          acceptTerms: callbackResult.acceptTerms,
        });

        trackEvent('slackSSO', { stage: 'exchange_succeeded' });
        trackEvent(
          callbackResult.intent === 'signUp' ? 'userSignedUp' : 'userSignedIn',
          { authMethod: 'Slack' },
        );

        const cleanReturnTo = `${returnTo.pathname}${returnTo.search}${returnTo.hash}`;
        const redirectPath = await getRoleBasedReturnTo(cleanReturnTo);
        navigate(redirectPath, { replace: true });
      } catch (error) {
        const failedIntent =
          intent ?? (await readSlackIntent(window.location.href));
        await clearSlackSigninState();

        trackEvent('slackSSO', {
          stage: 'exchange_failed',
          errorCode: getSlackSsoErrorCode(error),
        });
        // Without the stored state (e.g. the callback opened in another
        // browser) only the slackSSO event above records the failure.
        if (failedIntent) {
          trackEvent(
            failedIntent === 'signUp' ? 'signUpFailed' : 'signInFailed',
            {
              authMethod: 'Slack',
              reason:
                error instanceof SlackSsoError
                  ? 'slack_sso_error'
                  : getAuthFailureReason(error, 'Slack'),
            },
          );
        }

        const invalidTrialOffer =
          trialOfferCode && isInvalidTrialOfferError(error);

        setFailure({
          message: invalidTrialOffer
            ? trialOfferFailureMessage
            : getSlackSsoErrorMessage(error),
          signUpPath:
            trialOfferCode && !invalidTrialOffer
              ? `/sign-up?${new URLSearchParams({ [trialOfferParamKey]: trialOfferCode })}`
              : '/sign-up',
        });
      }
    };

    completeSignIn();
  }, [externalLoginMutation, navigate, trackEvent]);

  return <SlackCallbackStatus error={failure} />;
};
